import { Controller } from "@hotwired/stimulus"
import { gsap, Power4, CSSPlugin, ScrollToPlugin } from "gsap/all"

gsap.registerPlugin(CSSPlugin, ScrollToPlugin)

export default class extends Controller {
  static targets = ["title", "button", "overlay", "iframe", "close", "player"]
  static values = {
    provider: { type: String, default: "youtube" }, // "youtube" | "vimeo"
    videoId: String,
    muted: { type: Boolean, default: true },
    loop:  { type: Boolean, default: false }
  }

  connect() {
    this._wantPlayWithSound = false
    this._playerReady = false
    this._init()
  }

  disconnect() {
    if (this.providerValue === "youtube") {
      this.player?.destroy?.()
    } else {
      this.player?.unload?.().catch(() => {})
    }
  }

  async _init() {
    const provider = this.providerValue
    const id = this.videoIdValue
    if (!id) return

    if (provider === "youtube") {
      const YT = await loadYouTubeApiOnce()
      const playerVars = {
        autoplay: 1,
        mute: this.mutedValue ? 1 : 0,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        origin: window.location.origin,
        ...(this.hasStartValue ? { start: this.startValue } : {})
      }
      // Spécificité YouTube pour boucler une seule vidéo :
      // il faut loop=1 + playlist=<id>
      if (this.loopValue) Object.assign(playerVars, { loop: 1, playlist: id })

      this.player = new YT.Player(this.playerTarget, {
        videoId: id,
        playerVars,
        events: { onReady: () => this._onReady() }
      })
    } else {
      const Vimeo = await loadVimeoApiOnce()
      this.player = new Vimeo.Player(this.playerTarget, {
        id,
        autoplay: true,
        muted: this.mutedValue,
        loop: this.loopValue,
        controls: false,
        byline: false,
        title: false,
        portrait: false,
        dnt: true,
        playsinline: true,
        ...(this.hasStartValue ? { start: this.startValue } : {})
      })
      this.player.on("loaded", () => this._onReady())
    }
  }

  _onReady() {
    this._playerReady = true
    this._silentAutoplay().finally(() => {
      // Si jamais l'utilisateur a cliqué sur le bouton play avant que le
      // player ne soit prêt, on a stocké l'info pour ouvrir la vidéo en grand
      // une fois le player prêt
      if (this._wantPlayWithSound) this.expand()
    })
  }

  // Démarrage de la vidéo sans le son
  async _silentAutoplay() {
    try {
      if (this.providerValue === "youtube") {
        try { this.player.mute() } catch {/* no action */}
        try { this.player.playVideo() } catch {/* no action */}
      } else {
        try { await this.player.setMuted(true) } catch {/* no action */}
        try { await this.player.play() } catch {/* no action */}
      }
    } catch {/* no action */}
  }


  // Activation du son
  async _activateWithSound() {
    try {
      if (this.providerValue === "youtube") {
        try { this.player.unMute() } catch {/* no action */}
        try { this.player.playVideo() } catch {/* no action */}
      } else {
        try { await this.player.setMuted(false) } catch {/* no action */}
        try { await this.player.play() } catch {/* no action */}
      }
    } catch {/* no action */}
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("pointer-events-none", "opacity-0")
    }
  }

  // Arret de la vidéo
  _stopVideo() {
    if (this.providerValue === "youtube") {
      this.player.pauseVideo()
    } else {
      this.player.pause()
    }
  }

  // Ouverture de la vidéo en grand et activation du son
  expand () {
    if (!this._playerReady) { this._wantPlayWithSound = true; return }
    this._activateWithSound()

    gsap.to(this.closeTarget, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      ease: Power4.easeOut
    })

    // Animate and remove title
    if (this.hasTitleTarget) {
      gsap.to(this.titleTarget, {
        opacity: 0,
        y: -30,
        duration: 0.6,
        ease: Power4.easeOut
      })
    }

    // Animate and remove button
    gsap.to(this.buttonTarget, {
      opacity: 0,
      y: -30,
      duration: 0.6,
      ease: Power4.easeOut
    })

    gsap.to(this.overlayTarget, {
      opacity: 0,
      duration: 0.4,
      ease: Power4.easeOut
    })

    const tl = gsap.timeline()

    tl.to(this.element, {
      height: "100vh",
      duration: 1.3,
      ease: Power4.easeOut
    })
    .to(window, {
      duration: 1.3,
      ease: Power4.easeOut,
      scrollTo: {
        y: this.element.offsetTop,
        autoKill: false
      }
    }, "<")

    this.element.classList.add("z-max")
  }

  // Fermeture de la vidéo (réduction a son design d'origine) et mise sur pause
  close () {
    this._stopVideo()
    gsap.to(this.element, {
      height: "300px",
      duration: 0.8,
      ease: Power4.easeOut
    })

    gsap.to(window, {
    duration: 0.8,
    ease: Power4.easeOut,
    scrollTo: {
      y: this.element,
      offsetY: 64
    }
  })

    if (this.hasTitleTarget) {
      gsap.to(this.titleTarget, {
        opacity: 1,
        y: 0,
        duration: 0.4,
        ease: Power4.easeOut
      })
    }

    gsap.to(this.buttonTarget, {
      opacity: 1,
      y: 0,
      duration: 0.4,
      ease: Power4.easeOut
    })

    gsap.to(this.overlayTarget, {
      opacity: 0.2,
      duration: 0.4,
      ease: Power4.easeOut
    })

    gsap.to(this.closeTarget, {
      opacity: 0,
      y: -30,
      duration: 0.4,
      ease: Power4.easeOut
    })

    this.element.classList.remove("z-max")
  }
}

let ytApiReadyPromise
let vimeoApiReadyPromise

// Chargement de l'API youtube et vimeo en dehors du controller afin de ne le
// charger qu'une seule fois même s'il y a plusieurs instances de vidéos sur une page
function loadYouTubeApiOnce() {
  if (ytApiReadyPromise) return ytApiReadyPromise

  ytApiReadyPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT)

    const tag = document.createElement("script")
    tag.src = "https://www.youtube.com/iframe_api"
    document.head.appendChild(tag)

    window.onYouTubeIframeAPIReady = () => resolve(window.YT)
  })

  return ytApiReadyPromise
}

function loadVimeoApiOnce() {
  if (vimeoApiReadyPromise) return vimeoApiReadyPromise
  vimeoApiReadyPromise = new Promise((resolve) => {
    if (window.Vimeo?.Player) return resolve(window.Vimeo)
    const s = document.createElement("script")
    s.src = "https://player.vimeo.com/api/player.js"
    s.async = true
    s.onload = () => resolve(window.Vimeo)
    document.head.appendChild(s)
  })
  return vimeoApiReadyPromise
}
